import React, { useState, memo } from 'react';
import { Message } from '../../types/messages';
import { useMessageUrgency } from '../../hooks/useMessageUrgency';
import { ReplySection } from './ReplySection';
import '../../assets/css/components/message-item.css';

interface MessageItemProps {
  message: Message;
  onMarkAsRead: (messageId: number) => Promise<any>;
  showReply?: boolean;
}

/**
 * Card de mensagem - Compacto e expansível
 */
export const MessageItem = memo(function MessageItem({
  message,
  onMarkAsRead,
  showReply = false
}: MessageItemProps) {
  const { isResponseDeadlineExpired } = useMessageUrgency();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);

  const deadlineExpired = isResponseDeadlineExpired(message);
  const needsReply = message.requer_resposta && !message.resposta_enviada;
  const isRead = message.lida;

  // Formatar data
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'agora';
      if (diffMins < 60) return `${diffMins}min`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isRead || isMarkingAsRead) return;
    try {
      setIsMarkingAsRead(true);
      await onMarkAsRead(message.id);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Status class
  const getStatus = () => {
    if (deadlineExpired) return 'expired';
    if (needsReply && !isRead) return 'action';
    if (!isRead) return 'new';
    return 'read';
  };

  return (
    <article className={`mi ${getStatus()} ${isExpanded ? 'expanded' : ''}`}>
      {/* Linha compacta - clicável */}
      <div className="mi-header" onClick={handleToggle}>
        <div className="mi-left">
          <span className="mi-title">{message.titulo}</span>
          {!isExpanded && (
            <span className="mi-preview">{message.mensagem}</span>
          )}
        </div>
        <div className="mi-right">
          {needsReply && !deadlineExpired && <span className="mi-dot action" title="Requer resposta" />}
          {deadlineExpired && <span className="mi-dot expired" title="Expirado" />}
          <span className="mi-time">{formatDate(message.criada_em)}</span>
        </div>
      </div>

      {/* Conteúdo expandido */}
      {isExpanded && (
        <div className="mi-body">
          <p className="mi-content">{message.mensagem}</p>

          <div className="mi-meta">
            {message.criada_por_nome && <span>por {message.criada_por_nome}</span>}
            {message.expira_em && (
              <span className={deadlineExpired ? 'expired' : ''}>
                {deadlineExpired ? 'Expirou' : 'Expira'} {formatDate(message.expira_em)}
              </span>
            )}
          </div>

          <div className="mi-actions">
            {!isRead && (
              <button onClick={handleMarkAsRead} disabled={isMarkingAsRead} className="mi-btn">
                {isMarkingAsRead ? 'Marcando...' : 'Marcar como lida'}
              </button>
            )}
            {needsReply && showReply && !deadlineExpired && (
              <ReplySection
                messageId={message.id}
                respostaObrigatoria={message.resposta_obrigatoria || false}
                onReplySent={() => setIsExpanded(false)}
              />
            )}
            {deadlineExpired && needsReply && (
              <span className="mi-expired-msg">Prazo para resposta expirado</span>
            )}
            {message.resposta_enviada && (
              <span className="mi-done-msg">Resposta enviada</span>
            )}
          </div>
        </div>
      )}
    </article>
  );
});
